import * as Sentry from '@sentry/browser';
import { v4 } from 'uuid';
import type { components } from './data/.types/tea-events';
import type { TrackingEvents } from './data/tracking';
import { debounce } from './helpers/debounce';

export type PostEventsBody = {
  events: components['schemas']['FrontEndEvent'][];
};

export class TeaTracking {
  events: PostEventsBody['events'];
  debouncedSendEvents: () => void;

  constructor(
    private baseUrl: string,
    private accessToken: string,
    private meta: { [key: string]: unknown },
    private documentId: string,
  ) {
    this.events = [];

    this.recordEvent = this.recordEvent.bind(this);
    this.debouncedSendEvents = debounce(this.sendEvents.bind(this), 2000);
  }
  recordEvent(event: TrackingEvents) {
    this.events.push({
      id: v4(),
      timestamp: Math.floor(Date.now() / 1000),
      meta: this.meta,
      eventProperties: {
        ...event,
        documentId: this.documentId,
      },
    });
    this.debouncedSendEvents();
  }

  async sendEvents() {
    const events = this.events;
    this.events = [];

    if (!events || events.length === 0) {
      return;
    }

    const chunkSize = 50;
    const promises = [];
    let i = 0;
    while (i < events.length) {
      const chunk = events.slice(i, i + chunkSize);
      const sender = new TeaTrackingSender(
        chunk,
        this.baseUrl,
        this.accessToken,
      );
      promises.push(sender.sendEvents());
      i += chunkSize;
    }
    return Promise.all(promises);
  }

  async destroy() {
    return this.sendEvents();
  }
}

class TeaTrackingSender {
  attempt: number;
  maxRetries: number;

  constructor(
    public body: PostEventsBody['events'],
    public baseUrl: string,
    public accessToken: string,
  ) {
    this.attempt = 0;
    this.maxRetries = 3;
  }

  async sendEvents() {
    // early return if referer is storybook to prevent sending events / errors from here
    if (window.location.host === 'localhost:6006') {
      return;
    }

    try {
      const response = await fetch(`${this.baseUrl}/events`, {
        method: 'POST',
        body: JSON.stringify({ events: this.body }),
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          Authorization: `Bearer ${this.accessToken}`,
          Referer: window.location.toString(),
        },
      });

      if (!response.ok) {
        const error = new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`,
        );

        if (response.status === 401 || response.status === 403) {
          Sentry.captureException(error);
          return;
        }

        throw error;
      }

      return;
    } catch (error) {
      this.attempt += 1;

      if (this.attempt < this.maxRetries) {
        const backoffDelay = this.attempt * 100;
        setTimeout(() => {
          this.sendEvents();
        }, backoffDelay);
      } else {
        Sentry.captureException(error);
        console.error('Failed to send events after maximum retries', error);
      }
    }
  }
}
